import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { isEqual, uniqWith } from 'lodash';

import { Toast, useCustomerProducts } from 'helpers';
import { getProducts } from 'queries';
import { JsonDataItem, PrepaidProductStatus } from 'types';
import { BookingStore } from 'store';

export const useProductList = (customerId: number | null, callbackProductId: number | null) => {
  const { setProductList, setProductSelection, productSelection, job, serviceSelection } = BookingStore;
  const { isPrepaidProductsLoading, prepaidProducts, prepaidProductsIds } = useCustomerProducts(
    customerId,
    PrepaidProductStatus.New,
  );
  const [hasMultiplePrepaidSkills, setHasMultiplePrepaidSkills] = useState<boolean>(false);

  const {
    data,
    mutate: fetchProductList,
    isLoading: isProductsLoading,
    error: productListError,
    isSuccess: isproductListReveived,
  } = useMutation(getProducts, {
    onSuccess: (data) => setProductList(data),
    onError: (_: JsonDataItem) => {
      Toast.show({ description: 'Error trying to retrieve products', type: Toast.Type.Error });
    },
  });

  const getProductList = (formData: JsonDataItem) => {
    fetchProductList({
      customerType: formData?.accountType,
      customerId: customerId,
      serviceId: job?.ID,
      pricingCategory: formData?.pricingCategory,
      serviceType: formData?.serviceType || serviceSelection?.service_attributes?.job_service_type,
      productIds: callbackProductId ? [callbackProductId] : undefined,
    });
  };

  const isProductListLoading = isProductsLoading || isPrepaidProductsLoading;

  useEffect(() => {
    if (prepaidProductsIds?.length && data) {
      const isRequireMultipleSkills =
        uniqWith(
          prepaidProductsIds.map((id: number) => {
            const serviceSelection = data.find((p: JsonDataItem) => p.ID === id)?.service_selection;
            return Array.isArray(serviceSelection) ? serviceSelection.sort() : [serviceSelection];
          }),
          isEqual,
        ).length > 1;
      setHasMultiplePrepaidSkills(isRequireMultipleSkills);

      // preselect prepaid products only if withing the same skillset (service selection)
      !isRequireMultipleSkills &&
        setProductSelection({
          ...productSelection,
          products: prepaidProductsIds?.map((product: number) => {
            return { id: product.toString(), qty: 1 };
          }),
        });
    }
  }, [prepaidProductsIds, data]);

  return {
    prepaidProducts,
    prepaidProductsIds,
    hasMultiplePrepaidSkills,
    getProductList,
    isProductListLoading,
    productListError,
    isproductListReveived,
  };
};
